import { useState, useEffect, useCallback } from 'react';
import { VenuePhotoService, type ResolvedPhotosResponse } from '@/services/VenuePhotoService';
import type { PlacePhoto } from '@/services/PlacesService';

interface UseVenuePhotosResult {
  photos: PlacePhoto[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  hasCustomPhotos: boolean;
  source: 'custom' | 'google' | 'none';
}

const venuePhotoService = new VenuePhotoService();

/**
 * Hook to fetch venue photos — returns custom photos if they exist, else Google Places photos.
 * Drop-in replacement for useGooglePlacePhotos with venue-centric approach.
 *
 * @param venueId - Venue ID (primary identifier)
 * @param enabled - Whether to fetch
 * @param shareLinkToken - Optional share link token for public access
 * @param sessionId - Optional session ID for public access
 */
export function useVenuePhotos(
  venueId: string | null | undefined,
  enabled: boolean = true,
  shareLinkToken?: string,
  sessionId?: string,
): UseVenuePhotosResult {
  const [photos, setPhotos] = useState<PlacePhoto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<'custom' | 'google' | 'none'>('none');

  const fetchPhotos = useCallback(async () => {
    if (!venueId || !enabled) {
      setPhotos([]);
      setIsLoading(false);
      setError(null);
      setSource('none');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await venuePhotoService.getResolvedPhotos(venueId, 800, shareLinkToken, sessionId);
      setPhotos(data.photos || []);
      setSource(data.source || 'none');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load photos';
      setError(errorMessage);
      setPhotos([]);
      setSource('none');
      console.error('Error fetching venue photos:', err);
    } finally {
      setIsLoading(false);
    }
  }, [venueId, enabled, shareLinkToken, sessionId]);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  return {
    photos,
    isLoading,
    error,
    refetch: fetchPhotos,
    hasCustomPhotos: source === 'custom',
    source,
  };
}
